import { useRouterState } from "@tanstack/react-router";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

export type ActionVariant = "primary" | "outlined" | "text" | "default";

const DEFAULT_BUSY_TIMEOUT_MS = 4000;

interface BaseAction {
	id: string;
	disabled?: boolean;
}

export interface ButtonAction extends BaseAction {
	type: "button";
	label?: string;
	icon?: ReactNode;
	tooltip?: string;
	variant?: ActionVariant;
	danger?: boolean;
	onClick: () => void;
}

export interface SubmenuAction extends BaseAction {
	type: "submenu";
	label?: string;
	icon?: ReactNode;
	variant?: ActionVariant;
	danger?: boolean;
	submenu: ButtonAction[];
}

export interface CustomAction extends BaseAction {
	type: "custom";
	node: ReactNode;
	collapsedNode?: ReactNode;
}

export interface DividerAction extends BaseAction {
	type: "divider";
}

export type ActionItem =
	| ButtonAction
	| SubmenuAction
	| CustomAction
	| DividerAction;

export interface BusyState {
	message: string;
}

interface ActionsContextType {
	actions: ActionItem[];
	setActions: (actions: ActionItem[]) => void;
	busy: BusyState | null;
	/**
	 * Show a busy message next to the actions. Non-sticky messages auto-dismiss
	 * after `timeoutMs` (default {@link DEFAULT_BUSY_TIMEOUT_MS}); sticky ones stay
	 * until cleared with `setBusy(null)`.
	 */
	setBusy: (
		message: string | null,
		options?: { sticky?: boolean; timeoutMs?: number },
	) => void;
}

const ActionsContext = createContext<ActionsContextType | null>(null);

export function ActionsProvider({ children }: { children: ReactNode }) {
	const [actions, setActions] = useState<ActionItem[]>([]);
	const [busy, setBusyState] = useState<(BusyState & { path: string }) | null>(
		null,
	);
	const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
	// A busy message belongs to the page that set it — even a sticky one must not
	// survive navigation, so stamp it with the route and ignore it elsewhere.
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const pathRef = useRef(pathname);
	pathRef.current = pathname;

	const setBusy = useCallback<ActionsContextType["setBusy"]>(
		(message, options) => {
			if (timerRef.current) clearTimeout(timerRef.current);
			if (message === null) {
				setBusyState(null);
				return;
			}
			setBusyState({ message, path: pathRef.current });
			if (!options?.sticky) {
				timerRef.current = setTimeout(
					() => setBusyState(null),
					options?.timeoutMs ?? DEFAULT_BUSY_TIMEOUT_MS,
				);
			}
		},
		[],
	);

	useEffect(
		() => () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		},
		[],
	);

	return (
		<ActionsContext.Provider
			value={{
				actions,
				setActions,
				busy: busy?.path === pathname ? busy : null,
				setBusy,
			}}
		>
			{children}
		</ActionsContext.Provider>
	);
}

export function useActions(): ActionsContextType {
	const context = useContext(ActionsContext);
	if (context === undefined || context === null) {
		throw new Error("useActions must be used within a ActionsProvider");
	}
	return context;
}
